/**
 * Every campaign, for the list page. The tabs count and the search filters the
 * whole set on the client, so this follows the API's pages until it has them
 * all rather than showing only the first page.
 *
 * The key sits under the campaign list key, so a create or edit refreshes it.
 */
import type { TicketSalesCampaign } from '@bonsai/shared';
import { useQuery } from '@tanstack/react-query';

import { fetchTicketSalesCampaigns } from '../../api/ticket-sales-campaigns';

import { ticketSalesCampaignListKey } from './use-ticket-sales-campaigns';

/** The most the API returns in one page. */
export const CAMPAIGN_PAGE_LIMIT = 200;

/**
 * Keyed by id because a campaign created or deleted between two page requests
 * shifts the offsets, so a row can arrive on both pages.
 */
export async function fetchAllTicketSalesCampaigns(
  signal?: AbortSignal,
): Promise<TicketSalesCampaign[]> {
  const byId = new Map<string, TicketSalesCampaign>();
  for (let offset = 0; ; offset += CAMPAIGN_PAGE_LIMIT) {
    const page = await fetchTicketSalesCampaigns({ limit: CAMPAIGN_PAGE_LIMIT, offset }, signal);
    for (const campaign of page.campaigns) byId.set(campaign.id, campaign);
    const seen = offset + page.campaigns.length;
    if (page.campaigns.length === 0 || seen >= page.pagination.total) return [...byId.values()];
  }
}

export function useAllTicketSalesCampaigns() {
  return useQuery({
    queryKey: [...ticketSalesCampaignListKey(), 'all'],
    queryFn: ({ signal }) => fetchAllTicketSalesCampaigns(signal),
  });
}
